import React, { useRef, useState, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, Trash2, Crop as CropIcon, X, Loader2, Check } from 'lucide-react';
import { adminApi } from '@/services/api';
import { resolveImage } from '@/lib/imageUrl';
import { toast } from 'sonner';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Centra el crop inicial al cargar la imagen.
 */
const centerInitialCrop = (mediaWidth, mediaHeight, aspect) => {
  if (aspect) {
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
      mediaWidth,
      mediaHeight
    );
  }
  return centerCrop(
    { unit: '%', width: 90, height: 90, x: 5, y: 5 },
    mediaWidth,
    mediaHeight
  );
};

/**
 * Convierte el crop seleccionado a un Blob JPEG/PNG.
 */
const cropToBlob = (image, crop, mimeType = 'image/jpeg', quality = 0.9) =>
  new Promise((resolve, reject) => {
    const scaleX = image.naturalWidth  / image.width;
    const scaleY = image.naturalHeight / image.height;
    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(crop.width  * scaleX);
    canvas.height = Math.round(crop.height * scaleY);
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas no disponible'));
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      crop.x * scaleX, crop.y * scaleY,
      crop.width * scaleX, crop.height * scaleY,
      0, 0,
      canvas.width, canvas.height
    );
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('No se pudo generar la imagen'))),
      mimeType,
      quality
    );
  });

/**
 * Componente principal.
 *
 * Props:
 *  - value:       string actual (path o nombre de archivo) guardado en BD
 *  - onChange:    (newPath: string|null) => void
 *  - folder:      'products' | 'services' | 'processes' | 'suppliers'
 *  - legacyBase:  base externa para legacy (ej. /images/...)
 *  - aspect:      undefined | number (ej. 1, 16/9, 4/3) - default libre
 *  - label:       texto del label
 */
const ImageUploadCrop = ({ value, onChange, folder, legacyBase = '', aspect, label = 'Imagen' }) => {
  const inputRef    = useRef(null);
  const imgRef      = useRef(null);
  const [src, setSrc]               = useState(null);    // dataURL para crop
  const [crop, setCrop]             = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [originalMime, setOriginalMime] = useState('image/jpeg');

  const previewUrl = value ? resolveImage(value, legacyBase) : null;
  const isUploaded = value && (String(value).startsWith('/') || /^https?:/i.test(String(value)) === false ? false : false);
  // isUploaded = true si la imagen es de nuestros uploads (path /uploads/...)
  const isOwnUpload = typeof value === 'string' && value.startsWith('/uploads/');

  // ---------- Handlers ----------
  const handlePickFile = () => inputRef.current?.click();

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset para permitir re-selección del mismo archivo
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Formato no permitido. Usa JPG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('El archivo supera 5 MB.');
      return;
    }
    setOriginalMime(file.type);
    const reader = new FileReader();
    reader.onload = () => setSrc(String(reader.result));
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerInitialCrop(width, height, aspect));
  }, [aspect]);

  const handleConfirmCrop = async () => {
    if (!imgRef.current || !completedCrop || completedCrop.width < 10 || completedCrop.height < 10) {
      toast.error('Selecciona un área de recorte válida');
      return;
    }
    setUploading(true);
    try {
      const blob = await cropToBlob(imgRef.current, completedCrop, originalMime, 0.92);
      const ext  = originalMime === 'image/png'  ? 'png'
                 : originalMime === 'image/webp' ? 'webp'
                 : 'jpg';
      const file = new File([blob], `crop.${ext}`, { type: originalMime });
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.uploadImage(folder, fd);
      onChange(res.data.path);
      toast.success('Imagen subida');
      closeCropModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const closeCropModal = () => {
    setSrc(null);
    setCrop(null);
    setCompletedCrop(null);
  };

  const handleRemove = async () => {
    if (!window.confirm('¿Eliminar la imagen?')) return;
    if (isOwnUpload) {
      try { await adminApi.deleteImage(value); } catch { /* ignore */ }
    }
    onChange('');
  };

  // ---------- Render ----------
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-steel-dark">{label}</label>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFileSelected}
        className="hidden"
      />

      {previewUrl ? (
        <div className="relative inline-block group">
          <img
            src={previewUrl}
            alt="preview"
            className="w-40 h-40 object-cover rounded border border-gray-200"
            onError={(e) => { e.currentTarget.src = 'https://placehold.co/160x160/1A1A2E/E63946?text=NA'; }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors rounded flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={handlePickFile}
              className="bg-white text-steel-dark p-2 rounded-full hover:bg-accent-primary hover:text-white transition-colors"
              title="Cambiar imagen"
            >
              <Upload size={16} />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="bg-white text-red-600 p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          </div>
          {!isOwnUpload && (
            <p className="text-xs text-steel-gray mt-1 italic">Imagen externa (legacy)</p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePickFile}
          className="w-40 h-40 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center gap-2 text-steel-gray hover:border-accent-primary hover:text-accent-primary transition-colors"
        >
          <Upload size={28} />
          <span className="text-xs">Subir imagen</span>
          <span className="text-[10px] text-steel-gray">JPG, PNG o WebP</span>
        </button>
      )}

      {/* Modal de crop */}
      {src && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-steel-dark flex items-center gap-2">
                <CropIcon size={18} /> Recortar imagen
              </h3>
              <button
                type="button"
                onClick={closeCropModal}
                className="p-2 hover:bg-gray-100 rounded"
                disabled={uploading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={src}
                  alt="por recortar"
                  onLoad={onImageLoad}
                  style={{ maxHeight: '60vh' }}
                />
              </ReactCrop>
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-steel-gray">
                Arrastra las esquinas para recortar. Si no quieres recortar, deja la selección completa.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeCropModal}
                  disabled={uploading}
                  className="px-4 py-2 text-steel-gray hover:text-steel-dark"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCrop}
                  disabled={uploading}
                  className="bg-accent-primary text-white px-4 py-2 rounded hover:bg-accent-hover transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  {uploading ? 'Subiendo...' : 'Aplicar y subir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadCrop;
